import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminNotificationDispatchList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const NOTIFICATIONS_PAGE_SIZE = 30;
const EMAIL_SEARCH_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const notificationSortOptions = [
  { value: 'processed-desc', label: '送信日時が新しい順' },
  { value: 'processed-asc', label: '送信日時が古い順' },
  { value: 'category-asc', label: '種別昇順' },
  { value: 'category-desc', label: '種別降順' },
  { value: 'status-asc', label: '状態昇順' },
  { value: 'status-desc', label: '状態降順' },
  { value: 'recipient-asc', label: '宛先昇順' },
  { value: 'subject-asc', label: '件名昇順' },
];

const eventColumnStyle = {
  width: '13rem',
  minWidth: '13rem',
};

const subjectColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const recipientColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const statusColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};

const sourceColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const processedAtColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const actionColumnStyle = {
  width: '6rem',
  minWidth: '6rem',
};

const getSortableDateValue = (value) => {
  if (!value) {
    return 0;
  }

  const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
  const timestamp = Date.parse(normalized);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getStatusTone = (status) => {
  if (status === 'sent') {
    return 'published';
  }

  if (status === 'failed') {
    return 'draft';
  }

  return 'review';
};

const sortNotificationRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'processed-asc') {
      return getSortableDateValue(left.processedAt) - getSortableDateValue(right.processedAt);
    }

    if (sortKey === 'category-asc') {
      return left.categoryLabel.localeCompare(right.categoryLabel, 'ja');
    }

    if (sortKey === 'category-desc') {
      return right.categoryLabel.localeCompare(left.categoryLabel, 'ja');
    }

    if (sortKey === 'status-asc') {
      return left.statusLabel.localeCompare(right.statusLabel, 'ja');
    }

    if (sortKey === 'status-desc') {
      return right.statusLabel.localeCompare(left.statusLabel, 'ja');
    }

    if (sortKey === 'recipient-asc') {
      return left.recipientSummary.localeCompare(right.recipientSummary, 'ja');
    }

    if (sortKey === 'subject-asc') {
      return left.subject.localeCompare(right.subject, 'ja');
    }

    return getSortableDateValue(right.processedAt) - getSortableDateValue(left.processedAt);
  });

  return nextRows;
};

const AdminNotificationsListPage = () => {
  const content = yowanPageContent['/admin/notifications'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const recipientEmailQuery = searchParams.get('recipient_email') || '';
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState(recipientEmailQuery);
  const [sortKey, setSortKey] = useState('processed-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const trimmedSearchQuery = searchQuery.trim();
  const emailSearchQuery = EMAIL_SEARCH_PATTERN.test(trimmedSearchQuery)
    ? trimmedSearchQuery.toLowerCase()
    : '';
  const isEmailSearchActive = Boolean(emailSearchQuery);
  const recipientEmailFilter = isEmailSearchActive ? emailSearchQuery : recipientEmailQuery;

  React.useEffect(() => {
    setSearchQuery(recipientEmailQuery);
    setCurrentPage(1);
  }, [recipientEmailQuery]);

  React.useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminNotificationDispatchList({
      page: currentPage,
      pageSize: NOTIFICATIONS_PAGE_SIZE,
      recipientEmail: recipientEmailFilter,
    })
      .then((payload) => {
        if (isMounted) {
          setRows(payload.results);
          setTotalCount(payload.count);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRows([]);
          setTotalCount(0);
          setLoadError('通知履歴を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentPage, recipientEmailFilter]);

  if (!content) {
    return null;
  }

  const isUrlRecipientEmailOnlySearch = Boolean(recipientEmailQuery) && trimmedSearchQuery === recipientEmailQuery;
  const localSearchQuery = isEmailSearchActive || isUrlRecipientEmailOnlySearch
    ? ''
    : trimmedSearchQuery.toLowerCase();
  const filteredRows = localSearchQuery
    ? rows.filter((row) =>
        [
          row.categoryLabel,
          row.eventLabel,
          row.eventKey,
          row.statusLabel,
          row.subject,
          row.recipientSummary,
          row.sourceLabel,
          row.policyEventKey,
          row.templateKey,
          row.triggerContextSummary,
          row.onboardingRequestKindLabel,
          row.onboardingTargetKindLabel,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(localSearchQuery)),
      )
    : rows;
  const sortedRows = sortNotificationRows(filteredRows, sortKey);
  const totalPages = Math.max(1, Math.ceil(totalCount / NOTIFICATIONS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * NOTIFICATIONS_PAGE_SIZE;
  const visibleRows = sortedRows;
  const displayedTotal = localSearchQuery ? sortedRows.length : totalCount;
  const isRecipientEmailFiltered = Boolean(recipientEmailFilter);
  const filterMetaText = isRecipientEmailFiltered
    ? `宛先 ${recipientEmailFilter} の通知履歴を確認しています。`
    : '';

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="policies" to={appendYowanPreviewQueryIfNeeded('/admin/notifications/policies', preservedDomainSearch)}>
            通知ルールへ
          </Link>,
          <Link key="recipients" to={appendYowanPreviewQueryIfNeeded('/admin/notifications/recipients', preservedDomainSearch)}>
            通知宛先へ
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${displayedTotal}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="メールアドレス / イベント / 種別 / 依頼種別 / 件名 / 宛先 / 状態 / 送信元"
        sortOptions={notificationSortOptions}
      />
      {filterMetaText ? (
        <p className="yowan-admin-toolbar__meta">{filterMetaText}</p>
      ) : null}

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={eventColumnStyle}>種別 / 内容 / イベント</th>
              <th scope="col" style={subjectColumnStyle}>件名</th>
              <th scope="col" style={recipientColumnStyle}>宛先</th>
              <th scope="col" style={statusColumnStyle}>状態</th>
              <th scope="col" style={sourceColumnStyle}>送信元</th>
              <th scope="col" style={processedAtColumnStyle}>処理日時</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => (
              <tr key={row.id}>
                <td>{startIndex + index + 1}</td>
                <td style={eventColumnStyle}>
                  <p className="yowan-admin-resource-table__title">{row.categoryLabel}</p>
                  <p className="yowan-admin-resource-table__subline">
                    {[row.eventLabel, row.triggerContextSummary].filter(Boolean).join(' / ')}
                  </p>
                  <p className="yowan-admin-resource-table__subline">{row.eventKey}</p>
                </td>
                <td style={subjectColumnStyle}>
                  <p className="yowan-admin-resource-table__title">{row.subject}</p>
                </td>
                <td style={recipientColumnStyle}>{row.recipientSummary}</td>
                <td style={statusColumnStyle}>
                  <span
                    className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${getStatusTone(
                      row.status,
                    )}`}
                  >
                    {row.statusLabel}
                  </span>
                </td>
                <td style={sourceColumnStyle}>{row.sourceLabel}</td>
                <td style={processedAtColumnStyle}>{row.processedAt || '-'}</td>
                <td style={actionColumnStyle}>
                  <div className="yowan-admin-resource-table__actions">
                    <Link to={appendYowanPreviewQueryIfNeeded(row.detailHref, preservedDomainSearch)}>詳細</Link>
                  </div>
                </td>
              </tr>
            ))}
            {!visibleRows.length ? (
              <tr>
                <td colSpan="8">
                  {isLoading ? <LoadingSpinner size="sm" /> : loadError || '通知履歴はまだありません。'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminNotificationsListPage;
